import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Info,
  Users,
  FolderKanban,
  LayoutGrid,
  BarChart3,
  Settings2,
  UserPlus,
  ChevronDown,
  Save,
  X,
  Loader2,
  Pencil,
  Eye,
  Trash2,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { CompactKpiCard } from '@/components/kpi-card';
import { Card, CardContent } from '@/components/ui/card';
import { SettingsLayout } from '@/components/Pages/SettingsLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';

import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { MOCK_ROLES, MOCK_ROLE_USERS, MOCK_EMPLOYEES, countEnabledPermissions } from '@/lib/roles-mock-data';
import { fetchPlatformRole, savePlatformRole, assignEmployeeToRole, removeEmployeeFromRole, fetchEmployees, fetchEmployeeCurrentRole } from '@/lib/data-service';
import { useDataMode } from '@/contexts/DataModeContext';
import { usePermissions, PERMISSIONS_UPDATED_KEY } from '@/contexts/PermissionsContext';
import type { Role, RoleUser, ProjectTabKey, ProjectTabAccess } from '@/types/roles.types';

// ============================================================================
// Tab definitions
// ============================================================================

type EditRoleTab =
  | 'details'
  | 'projects'
  | 'project-tabs'
  | 'reports'
  | 'settings'
  | 'users';

// ============================================================================
// AccordionSection — matches Project Settings pattern exactly
// ============================================================================

interface AccordionSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function AccordionSection({
  icon,
  title,
  description,
  defaultOpen = true,
  children,
}: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-accent/40 transition-colors text-left group"
        aria-expanded={open}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="border-t border-border px-6 py-5">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Permission count badge helper
// ============================================================================

function PermBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <Badge variant="secondary" className="ml-1.5 text-xs font-mono min-w-[1.5rem] text-center">
      {count}
    </Badge>
  );
}

// ============================================================================
// Project tab rows config
// ============================================================================

const PROJECT_TAB_ROWS: Array<{
  key: ProjectTabKey;
  label: string;
  description: string;
  /** Access levels selectable for this tab. Some tabs have no edit
   *  functionality (so 'Edit' is meaningless) while others always allow
   *  editing when visible (so 'View' has no effect). */
  options: ProjectTabAccess[];
}> = [
  { key: 'overview', label: 'Overview', description: 'Summary of project status, progress and key metrics.', options: ['Hidden', 'View', 'Edit'] },
  { key: 'financials', label: 'Financials', description: 'Revenue, costs and overall financial performance.', options: ['Hidden', 'View'] },
  { key: 'billing', label: 'Billing', description: 'Invoices, billing schedules and payment tracking.', options: ['Hidden', 'Edit'] },
  { key: 'salesBudget', label: 'Sales Budget', description: 'Sales targets and forecast vs actuals.', options: ['Hidden', 'Edit'] },
  { key: 'costBudget', label: 'Cost Budget', description: 'Cost planning, budgets and variance analysis.', options: ['Hidden', 'Edit'] },
  { key: 'resourceBudget', label: 'Resource Budget', description: 'Planned vs actual resource spend and utilisation.', options: ['Hidden', 'Edit'] },
  { key: 'costCentres', label: 'Cost Centres', description: 'Cost centre allocations and breakdowns.', options: ['Hidden', 'Edit'] },
  { key: 'purchases', label: 'Purchases', description: 'Purchase orders, supplier costs and approvals.', options: ['Hidden', 'View'] },
  { key: 'time', label: 'Time', description: 'Timesheets and logged hours against the project.', options: ['Hidden', 'View'] },
  { key: 'expenses', label: 'Expenses', description: 'Expense claims and reimbursements for the project.', options: ['Hidden', 'View'] },
  { key: 'team', label: 'Team', description: 'Team members assigned and their roles on the project.', options: ['Hidden', 'View', 'Edit'] },
  { key: 'resourcing', label: 'Resourcing', description: 'Resource allocations, capacity and scheduling.', options: ['Hidden', 'View'] },
  { key: 'milestones', label: 'Milestones', description: 'Key project milestones, dates and completion status.', options: ['Hidden', 'View'] },
  { key: 'changeRequests', label: 'Change Requests', description: 'Scope changes, approvals and impact tracking.', options: ['Hidden', 'View'] },
  { key: 'audit', label: 'Audit', description: 'Activity log and change history for the project.', options: ['Hidden', 'View'] },
  { key: 'reports', label: 'Reports', description: 'Project-level reports and exportable summaries.', options: ['Hidden', 'View'] },
];



// ============================================================================
// Permission toggle row — used in Reports & Settings tabs
// ============================================================================

interface PermToggleRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}

// Card variant — bordered, used in 2-column grid layouts
function PermToggleCard({ label, description, checked, onChange, disabled }: PermToggleRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        aria-label={label}
        className="mt-0.5 shrink-0"
      />
    </div>
  );
}

// ============================================================================
// AddUsersDialog
// ============================================================================

interface AddUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  availableEmployees: Array<{ id: number; name: string; email: string }>;
  isLoadingEmployees: boolean;
  onUsersAdded: (ids: number[]) => Promise<void>;
}

function AddUsersDialog({
  open,
  onOpenChange,
  roleName,
  availableEmployees,
  isLoadingEmployees,
  onUsersAdded,
}: AddUsersDialogProps) {
  const [dialogSearch, setDialogSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDialogSearch('');
    setSelectedIds(new Set());
  }, [open]);

  const filteredUsers = dialogSearch.trim()
    ? availableEmployees.filter(
        (u) =>
          u.name.toLowerCase().includes(dialogSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(dialogSearch.toLowerCase()),
      )
    : availableEmployees;

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.has(u.id));
  const someFilteredSelected =
    filteredUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected;

  function toggleUser(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllFiltered() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredUsers.forEach((u) => next.delete(u.id));
      } else {
        filteredUsers.forEach((u) => next.add(u.id));
      }
      return next;
    });
  }

  async function handleConfirmAdd() {
    if (selectedIds.size === 0) return;
    setIsAdding(true);
    try {
      await onUsersAdded(Array.from(selectedIds));
      onOpenChange(false);
    } catch {
      toast.error('Failed to add some users');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]">
        <DialogHeader className="px-5 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Add Users to {roleName}
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            Select one or more users to assign to this role.
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="px-4 py-3 border-b shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search by name or email…"
              value={dialogSearch}
              onChange={(e) => setDialogSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Select all row */}
        {!isLoadingEmployees && filteredUsers.length > 0 && (
          <div
            className="flex items-center gap-3 px-4 py-2 bg-muted/40 border-b shrink-0 cursor-pointer hover:bg-muted/60 transition-colors"
            onClick={toggleAllFiltered}
          >
            <Checkbox
              checked={allFilteredSelected}
              data-state={someFilteredSelected ? 'indeterminate' : undefined}
              onCheckedChange={toggleAllFiltered}
              className="pointer-events-none"
            />
            <span className="text-xs font-medium text-muted-foreground">
              {allFilteredSelected
                ? 'Deselect all'
                : someFilteredSelected
                  ? `${selectedIds.size} selected — select all ${filteredUsers.length}`
                  : `Select all ${filteredUsers.length} user${filteredUsers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}

        {/* User list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoadingEmployees ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center gap-2 px-4">
              {dialogSearch ? (
                <>
                  <Search className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No users match &ldquo;{dialogSearch}&rdquo;</p>
                </>
              ) : (
                <>
                  <Users className="h-7 w-7 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">All users already assigned</p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredUsers.map((user) => {
                const isSelected = selectedIds.has(user.id);
                // Interim: flag users who are already in another role.
                // Once backend returns currentRole per employee this will drive the move-confirmation flow.
                const hasExistingRole = !!(user as { currentRole?: string }).currentRole;
                return (
                  <div
                    key={user.id}
                    className={cn(
                      'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
                    )}
                    onClick={() => toggleUser(user.id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleUser(user.id)}
                      className="pointer-events-none shrink-0"
                    />
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {user.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium leading-tight truncate">{user.name}</span>
                        {hasExistingRole && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning-100 px-2 py-0.5 text-[10px] font-medium text-warning-700 border border-warning-200 shrink-0">
                            <AlertTriangle className="h-2.5 w-2.5" />
                            Currently in: {(user as { currentRole?: string }).currentRole}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-4 py-3 border-t shrink-0 gap-2 sm:gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirmAdd}
            disabled={selectedIds.size === 0 || isAdding}
            className="gap-1.5"
          >
            {isAdding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserPlus className="h-3.5 w-3.5" />
            )}
            {selectedIds.size > 0
              ? `Add ${selectedIds.size} User${selectedIds.size !== 1 ? 's' : ''}`
              : 'Add Users'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// BulkRemoveBar
// ============================================================================

function BulkRemoveBar({
  selectedCount,
  onRemove,
  onClear,
  isRemoving,
}: {
  selectedCount: number;
  onRemove: () => void;
  onClear: () => void;
  isRemoving: boolean;
}) {
  if (selectedCount === 0) return null;
  return (
    <div className="sticky bottom-0 left-0 right-0 z-10 pointer-events-none">
      <div className="flex justify-center pb-2">
        <div className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground shadow-2xl text-sm whitespace-nowrap animate-in slide-in-from-bottom-4 duration-300">
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/25 text-xs font-bold px-1">
            {selectedCount}
          </span>
          <span className="font-medium pr-1">
            {selectedCount === 1 ? 'user' : 'users'} selected
          </span>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onRemove}
            disabled={isRemoving}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 transition-colors font-medium disabled:opacity-60"
          >
            {isRemoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Remove from Role
          </button>
          <div className="h-4 w-px bg-white/30" />
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-white/80 hover:text-white transition-colors px-1"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
            <span className="text-xs">Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Edit Role Page
// ============================================================================

interface SettingsEditRolePageProps {
  /** When rendered inline (e.g. inside the onboarding wizard), the role id is
   * passed as a prop instead of being read from the route params. */
  roleId?: string;
  /** Custom back handler used when rendered inline; defaults to route navigation. */
  onBack?: () => void;
}

export default function SettingsEditRolePage({ roleId: roleIdProp, onBack }: SettingsEditRolePageProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ roleId: string }>();
  const roleId = roleIdProp ?? params.roleId;
  const goBack = onBack ?? (() => navigate('/settings/user-permissions'));
  const { isDemoMode } = useDataMode();
  const { refetchPermissions } = usePermissions();

  // Find role from mock data in demo mode, otherwise start null and load from API
  const baseRole = useMemo(
    () => isDemoMode ? (MOCK_ROLES.find((r) => r.id === roleId) ?? null) : null,
    [roleId, isDemoMode],
  );

  // Local editable state
  const [role, setRole] = useState<Role | null>(() => baseRole ? { ...baseRole, permissions: { ...baseRole.permissions, projectTabs: { ...baseRole.permissions.projectTabs }, projectActions: { ...baseRole.permissions.projectActions }, reports: { ...baseRole.permissions.reports }, settings: { ...baseRole.permissions.settings } } } : null);
  const [savedRole, setSavedRole] = useState<Role | null>(baseRole);
  const [users, setUsers] = useState<RoleUser[]>(() => isDemoMode ? (MOCK_ROLE_USERS[roleId ?? ''] ?? []) : []);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(!isDemoMode);
  const [activeTab, setActiveTab] = useState<EditRoleTab>('details');
  const [removeUserTarget, setRemoveUserTarget] = useState<RoleUser | null>(null);
  const [allEmployees, setAllEmployees] = useState<Array<{ id: number; name: string; email: string }>>(() => isDemoMode ? MOCK_EMPLOYEES : []);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  // Add users dialog
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // Bulk remove
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<{ employee: { id: number; name: string; email: string }; currentRoleName: string } | null>(null);

  // Load role from API on mount (live mode only)
  useEffect(() => {
    const loadRole = async () => {
      if (isDemoMode || !roleId) return;
      try {
        const apiRole = await fetchPlatformRole(roleId);
        if (apiRole) {
          setRole(apiRole);
          setSavedRole(apiRole);
          if (apiRole.users) {
            setUsers(apiRole.users.map(u => ({
              id: String(u.id),
              name: u.name,
              email: u.email,
              lastActive: u.assignedAt ?? null,
            })));
          }
        }
      } catch (err) {
        console.error('Failed to load role:', err);
      } finally {
        setIsLoadingRole(false);
      }
    };
    loadRole();
  }, [roleId, isDemoMode]);

  // Load all employees when Users tab is active (live mode)
  useEffect(() => {
    if (activeTab !== 'users' || isDemoMode || allEmployees.length > 0) return;
    const loadEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const emps = await fetchEmployees('employees');
        setAllEmployees(
          (emps ?? []).map((e: { id: number; name?: string; firstName?: string; lastName?: string; email?: string }) => ({
            id: e.id,
            name: e.name || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim(),
            email: e.email ?? '',
          }))
        );
      } catch (err) {
        console.error('Failed to load employees:', err);
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    loadEmployees();
  }, [activeTab, isDemoMode, allEmployees.length]);

  // ── Available employees for add user (exclude already-assigned) ───────────

  // Employees not yet assigned to this role — passed to the Add Users dialog
  const availableEmployees = useMemo(() => {
    const assignedIds = new Set(users.map((u) => u.id));
    return allEmployees.filter((e) => !assignedIds.has(String(e.id)));
  }, [allEmployees, users]);

  // ── Loading / Not found ──────────────────────────────────────────────────────
  if (!role) {
    return (
      <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
        <div className="p-6 space-y-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Roles
          </Button>
          <p className="text-muted-foreground">
            {isLoadingRole ? 'Loading role…' : 'Role not found.'}
          </p>
        </div>
      </SettingsLayout>
    );
  }

  // ── Derived counts for tab badges ─────────────────────────────────────────

  const projectsEnabledCount =
    Object.values(role.permissions.projectActions).filter(Boolean).length;

  const projectTabsEnabledCount =
    Object.values(role.permissions.projectTabs).filter((v) => v !== 'Hidden').length;

  const reportsEnabledCount =
    Object.values(role.permissions.reports).filter(Boolean).length;

  const settingsEnabledCount =
    Object.values(role.permissions.settings).filter(Boolean).length;

  const totalEnabled = countEnabledPermissions(role);

  // ── Updaters ─────────────────────────────────────────────────────────────

  function updateRole(patch: Partial<Role>) {
    setRole((prev) => prev ? { ...prev, ...patch } : prev);
    setHasChanges(true);
  }

  function updateProjectVisibility(value: Role['permissions']['projectVisibility']) {
    setRole((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: { ...prev.permissions, projectVisibility: value },
      };
    });
    setHasChanges(true);
  }

  function updatePermission<K extends Exclude<keyof Role['permissions'], 'projectVisibility'>>(
    section: K,
    patch: Partial<Role['permissions'][K]>,
  ) {
    setRole((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [section]: { ...(prev.permissions[section] as object), ...patch },
        },
      };
    });
    setHasChanges(true);
  }

  // ── Save ─────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!role) return;
    setIsSaving(true);
    try {
      if (!isDemoMode) {
        await savePlatformRole(role.id, role);
        // Signal all tabs (including this one) that role permissions changed.
        // The PermissionsContext listens for this via `storage` / `visibilitychange`
        // events and re-fetches the current user's permissions automatically.
        try { localStorage.setItem(PERMISSIONS_UPDATED_KEY, Date.now().toString()); } catch { /* quota / private mode */ }
        await refetchPermissions();
      }
      setSavedRole(role);
      setHasChanges(false);
      toast.success('Role settings saved.');
    } catch (err) {
      console.error('Failed to save role settings:', err);
      toast.error('Failed to save role settings.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleDiscard() {
    const source = savedRole ?? baseRole;
    if (source) {
      setRole({ ...source, permissions: { ...source.permissions, projectTabs: { ...source.permissions.projectTabs }, projectActions: { ...source.permissions.projectActions }, reports: { ...source.permissions.reports }, settings: { ...source.permissions.settings } } });
    }
    setHasChanges(false);
  }

  // ── Bulk add users (called by AddUsersDialog) ────────────────────────────

  async function handleUsersAdded(ids: number[]) {
    if (!role) return;
    setIsAssigning(true);
    try {
      if (!isDemoMode) {
        await Promise.all(ids.map((id) => assignEmployeeToRole(role.id, id)));
      }
      const newUsers = ids.map((id) => {
        const emp = allEmployees.find((e) => e.id === id)!;
        return { id: String(id), name: emp.name, email: emp.email, lastActive: null };
      });
      setUsers((prev) => [...prev, ...newUsers]);
      toast.success(
        ids.length === 1
          ? `${newUsers[0].name} added to this role.`
          : `${ids.length} users added to this role.`,
      );
    } catch (err) {
      console.error('Failed to assign employees:', err);
      toast.error('Failed to add some users to the role.');
      throw err; // re-throw so the dialog can handle its own loading state
    } finally {
      setIsAssigning(false);
    }
  }

  async function executeAddUser(employee: { id: number; name: string; email: string }) {
    if (!role) return;
    setIsAssigning(true);
    try {
      if (!isDemoMode) {
        await assignEmployeeToRole(role.id, employee.id);
      }
      setUsers((prev) => [
        ...prev,
        {
          id: String(employee.id),
          name: employee.name,
          email: employee.email,
          lastActive: null,
        },
      ]);
      toast.success(`${employee.name} added to this role.`);
    } catch (err) {
      console.error('Failed to assign employee:', err);
      toast.error('Failed to add user to role.');
    } finally {
      setIsAssigning(false);
    }
  }

  async function confirmReassignUser() {
    if (!reassignTarget) return;
    setReassignTarget(null);
    await executeAddUser(reassignTarget.employee);
  }

  // ── Single remove user ───────────────────────────────────────────────────

  async function confirmRemoveUser() {
    if (!removeUserTarget || !role) return;
    try {
      if (!isDemoMode) {
        await removeEmployeeFromRole(role.id, Number(removeUserTarget.id));
      }
      setUsers((prev) => prev.filter((u) => u.id !== removeUserTarget.id));
      toast.success(`${removeUserTarget.name} removed from this role.`);
    } catch (err) {
      console.error('Failed to remove employee:', err);
      toast.error('Failed to remove user from role.');
    } finally {
      setRemoveUserTarget(null);
    }
  }

  // ── Bulk remove users ────────────────────────────────────────────────────

  async function confirmBulkRemove() {
    if (!role || bulkSelectedIds.size === 0) return;
    setIsBulkRemoving(true);
    try {
      if (!isDemoMode) {
        await Promise.all(
          Array.from(bulkSelectedIds).map((id) => removeEmployeeFromRole(role.id, Number(id))),
        );
      }
      setUsers((prev) => prev.filter((u) => !bulkSelectedIds.has(u.id)));
      toast.success(
        bulkSelectedIds.size === 1
          ? 'User removed from this role.'
          : `${bulkSelectedIds.size} users removed from this role.`,
      );
      setBulkSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to bulk remove employees:', err);
      toast.error('Failed to remove some users from the role.');
    } finally {
      setIsBulkRemoving(false);
      setShowBulkRemoveConfirm(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const isPlatform = role.type === 'Platform';

  return (
    <SettingsLayout currentPath={location.pathname} onNavigate={navigate}>
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6 p-6">

            {/* ── Back + Header ─────────────────────────────────────────── */}
            <div className="space-y-1">
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 text-muted-foreground hover:text-foreground"
                onClick={goBack}
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Roles &amp; Permissions
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{role.name}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Configure role details and permissions
                </p>
              </div>
            </div>

            {/* ── Tabs ─────────────────────────────────────────────────── */}
            <Tabs
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as EditRoleTab)}
              className="space-y-6"
            >
              <TabsList className="w-full justify-start">
                <TabsTrigger value="details" className="flex items-center gap-1.5 flex-none">
                  <Info className="h-4 w-4" />
                  Details
                </TabsTrigger>
                <TabsTrigger value="projects" className="flex items-center gap-1.5 flex-none">
                  <FolderKanban className="h-4 w-4" />
                  Projects
                  <PermBadge count={projectsEnabledCount} />
                </TabsTrigger>
                <TabsTrigger value="project-tabs" className="flex items-center gap-1.5 flex-none">
                  <LayoutGrid className="h-4 w-4" />
                  Project Tabs
                  <PermBadge count={projectTabsEnabledCount} />
                </TabsTrigger>
                <TabsTrigger value="reports" className="flex items-center gap-1.5 flex-none">
                  <BarChart3 className="h-4 w-4" />
                  Reports &amp; Analytics
                  <PermBadge count={reportsEnabledCount} />
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-1.5 flex-none">
                  <Settings2 className="h-4 w-4" />
                  Settings
                  <PermBadge count={settingsEnabledCount} />
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-1.5 flex-none">
                  <Users className="h-4 w-4" />
                  Users
                  <PermBadge count={users.length} />
                </TabsTrigger>
              </TabsList>

              {/* ════════════════════════════════════════════════════════════
                  Details Tab
              ════════════════════════════════════════════════════════════ */}
              <TabsContent value="details" className="mt-0 space-y-4">

                {/* Role Details — consolidated */}
                <AccordionSection
                  icon={<Pencil className="h-5 w-5 text-primary" />}
                  title="Role Details"
                  description="Configure the basic information for this role."
                >
                  <div className="space-y-4">
                    {/* Top row: Role Name (2/3) + Active checkbox (1/3) */}
                    <div className="grid grid-cols-3 gap-4">
                      {/* Role Name */}
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="role-name">
                          Role Name {!isPlatform && <span className="text-destructive">*</span>}
                        </Label>
                        <Input
                          id="role-name"
                          value={role.name}
                          onChange={(e) => updateRole({ name: e.target.value })}
                          placeholder="e.g. Senior Project Manager"
                          disabled={isPlatform}
                          readOnly={isPlatform}
                        />
                        {isPlatform ? (
                          <p className="text-xs text-muted-foreground">
                            Platform role names cannot be changed.
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            The display name used across the platform for this role.
                          </p>
                        )}
                      </div>

                      {/* Active checkbox */}
                      <div className="col-span-1 flex items-start justify-between gap-4 rounded-lg border border-border p-4">
                        <div className="space-y-1">
                          <Label htmlFor="role-active" className="text-sm font-medium cursor-pointer">
                            Active
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Enable this role to make it available for assignment to users.
                          </p>
                        </div>
                        <Checkbox
                          id="role-active"
                          checked={role.status === 'Active'}
                          onCheckedChange={(val) =>
                            updateRole({ status: (val as boolean) ? 'Active' : 'Inactive' })
                          }
                        />
                      </div>
                    </div>

                    {/* Second row: Description full width */}
                    <div className="space-y-2">
                      <Label htmlFor="role-description">Description</Label>
                      <Textarea
                        id="role-description"
                        value={role.description}
                        onChange={(e) => updateRole({ description: e.target.value })}
                        placeholder="Describe this role's purpose and responsibilities..."
                        rows={4}
                      />
                      <p className="text-xs text-muted-foreground">
                        A brief description of what this role can do.
                      </p>
                    </div>
                  </div>
                </AccordionSection>

                {/* Summary card */}
                <AccordionSection
                  icon={<Info className="h-5 w-5 text-primary" />}
                  title="Summary"
                  description="Overview of users and permissions for this role."
                >
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {/* Users assigned */}
                      <button
                        type="button"
                        className="text-left"
                        onClick={() => setActiveTab('users')}
                      >
                        <CompactKpiCard
                          title="Users Assigned"
                          value={users.length}
                          description="Click to view users"
                          icon={Users}
                          accentVariant="info"
                          className="shadow-none"
                        />
                      </button>

                      {/* Total permissions */}
                      <CompactKpiCard
                        title="Permissions Enabled"
                        value={totalEnabled}
                        description="Active permissions"
                        icon={LayoutGrid}
                        accentVariant="info"
                        className="shadow-none"
                      />

                      {/* Status */}
                      <div className="rounded-lg border border-border p-4">
                        <Badge
                          variant={role.status === 'Active' ? 'default' : 'secondary'}
                          className={
                            role.status === 'Active'
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200 text-sm px-3 py-1'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-100 border-slate-200 text-sm px-3 py-1'
                          }
                        >
                          {role.status}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-2">Role status</p>
                      </div>
                    </div>

                    {/* Permissions by category */}
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Permissions by Category</p>
                      {[
                        {
                          label: 'Project Actions',
                          enabled: projectsEnabledCount,
                          total: Object.keys(role.permissions.projectActions).length,
                        },
                        {
                          label: 'Project Tabs',
                          enabled: projectTabsEnabledCount,
                          total: PROJECT_TAB_ROWS.length,
                        },
                        {
                          label: 'Reports & Analytics',
                          enabled: reportsEnabledCount,
                          total: Object.keys(role.permissions.reports).length,
                        },
                        {
                          label: 'Settings Access',
                          enabled: settingsEnabledCount,
                          total: Object.keys(role.permissions.settings).length,
                        },
                      ].map((cat) => {
                        const pct = cat.total > 0 ? Math.round((cat.enabled / cat.total) * 100) : 0;
                        return (
                          <div key={cat.label} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{cat.label}</span>
                              <span className="font-medium tabular-nums">
                                {cat.enabled}/{cat.total}
                              </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </AccordionSection>
              </TabsContent>

              {/* ════════════════════════════════════════════════════════════
                  Projects Tab
              ════════════════════════════════════════════════════════════ */}
              <TabsContent value="projects" className="mt-0 space-y-4">

                {/* Project Visibility */}
                <AccordionSection
                  icon={<Eye className="h-5 w-5 text-primary" />}
                  title="Project Visibility"
                  description="Controls how many projects users with this role can see."
                >
                  <div className="space-y-4">
                    {[
                      {
                        value: 'all' as const,
                        title: 'All Projects',
                        description: 'Users can see every project in the company.',
                      },
                      {
                        value: 'managed-allocated' as const,
                        title: 'Managed & Allocated Projects',
                        description:
                          'Users can only see projects where they are explicitly assigned via Key People or the Resource Manager scheduler.',
                      },
                    ].map((option, idx, arr) => (
                      <div key={option.value}>
                        <div
                          className="flex items-center justify-between gap-4 cursor-pointer"
                          onClick={() => updateProjectVisibility(option.value)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm font-semibold cursor-pointer">
                                {option.title}
                              </Label>
                            </div>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {option.description}
                            </p>
                          </div>
                          <input
                            type="radio"
                            name="projectVisibility"
                            checked={role.permissions.projectVisibility === option.value}
                            onChange={() => updateProjectVisibility(option.value)}
                            className="h-4 w-4 shrink-0 border-neutral-300 text-primary focus:ring-primary"
                          />
                        </div>
                        {idx < arr.length - 1 && <Separator className="mt-4" />}
                      </div>
                    ))}
                  </div>
                </AccordionSection>

                {/* Project Actions */}
                <AccordionSection
                  icon={<FolderKanban className="h-5 w-5 text-primary" />}
                  title="Project Actions"
                  description="Individual actions users with this role can perform on projects."
                >
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'create' as const, label: 'Create new project', description: 'Can create new projects in the system.' },
                      { key: 'edit' as const, label: 'Edit project details', description: 'Can edit project name, dates, and general details.' },
                      { key: 'delete' as const, label: 'Delete project', description: 'Can soft delete / archive projects.' },
                      { key: 'changeStatus' as const, label: 'Change project status', description: 'Can move projects between status stages.' },
                      { key: 'assignKeyPeople' as const, label: 'Assign Key People to projects', description: 'Can assign users to Key People roles on projects.' },
                    ].map(({ key, label, description }) => (
                      <PermToggleCard
                        key={key}
                        label={label}
                        description={description}
                        checked={role.permissions.projectActions[key]}
                        onChange={(val) => updatePermission('projectActions', { [key]: val })}
                      />
                    ))}
                  </div>
                </AccordionSection>
              </TabsContent>

              {/* ════════════════════════════════════════════════════════════
                  Project Tabs Tab
              ════════════════════════════════════════════════════════════ */}
              <TabsContent value="project-tabs" className="mt-0 space-y-4">
                <AccordionSection
                  icon={<LayoutGrid className="h-5 w-5 text-primary" />}
                  title="Project Tab Access"
                  description="Control what users with this role can see and do on each project tab."
                >
                  <div className="grid grid-cols-2 gap-3">
                    {PROJECT_TAB_ROWS.map(({ key, label, description, options }) => {
                      const stored = role.permissions.projectTabs[key];
                      const isOverview = key === 'overview';
                      // A previously-saved value may no longer be a selectable
                      // option for this tab. Fall back to the nearest equivalent
                      // (a non-Hidden stored value maps to the non-Hidden option)
                      // so the control never renders blank.
                      const current = options.includes(stored)
                        ? stored
                        : stored === 'Hidden'
                          ? 'Hidden'
                          : options.find((o) => o !== 'Hidden') ?? options[0];
                      return (
                        <div
                          key={key}
                          className="flex items-start justify-between gap-4 rounded-lg border border-border p-3"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {isOverview ? 'Always visible — cannot be hidden' : description}
                            </p>
                          </div>
                          <select
                            value={current}
                            onChange={(e) =>
                              updatePermission('projectTabs', {
                                [key]: e.target.value as ProjectTabAccess,
                              })
                            }
                            className="mt-0.5 w-24 shrink-0 rounded-md border border-input bg-background px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                            aria-label={`${label} access level`}
                          >
                            {options
                              .filter((o) => !(isOverview && o === 'Hidden'))
                              .map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </AccordionSection>
              </TabsContent>

              {/* ════════════════════════════════════════════════════════════
                  Reports & Analytics Tab
              ════════════════════════════════════════════════════════════ */}
              <TabsContent value="reports" className="mt-0 space-y-4">
                <AccordionSection
                  icon={<BarChart3 className="h-5 w-5 text-primary" />}
                  title="Reports & Analytics Access"
                  description="Control which dashboards and report pages are accessible to users with this role."
                >
                  <div className="space-y-5">
                    {/* Dashboard Access group */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
                        Dashboard Access
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          {
                            key: 'executiveDashboard' as const,
                            label: 'Executive Dashboard',
                            description: 'Shows the Executive Dashboard. If PM Dashboard is also enabled, PM widgets are available in the dashboard settings cog.',
                          },
                          {
                            key: 'pmDashboard' as const,
                            label: 'PM Dashboard',
                            description: 'Shows the Project Manager Dashboard. If Executive Dashboard is also enabled, the Executive Dashboard takes priority and PM widgets appear in the settings cog.',
                          },
                        ].map(({ key, label, description }) => (
                          <PermToggleCard
                            key={key}
                            label={label}
                            description={description}
                            checked={role.permissions.reports[key]}
                            onChange={(val) => updatePermission('reports', { [key]: val })}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-border" />

                    {/* Report Access group */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
                        Report Access
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { key: 'plReport' as const, label: 'P&L Report', description: 'Access to the Profit & Loss report.' },
                          { key: 'wipReport' as const, label: 'WIP Report', description: 'Access to the Work in Progress report.' },
                          { key: 'utilisationReport' as const, label: 'Utilisation Report', description: 'Access to the Utilisation report.' },
                          { key: 'budgetVarianceReport' as const, label: 'Budget Variance Report', description: 'Access to the Budget Variance report.' },
                          { key: 'auditLogReport' as const, label: 'Audit Log Report', description: 'Access to the company-wide Audit Log report.' },
                          { key: 'projectTransactionsReport' as const, label: 'Project Transactions Report', description: 'Access to the Project Transactions report.' },
                          { key: 'projectStatusReport' as const, label: 'Project Status Report', description: 'Access to the Project Status report.' },
                          { key: 'projectForecastReport' as const, label: 'Project Forecast Report', description: 'Access to the Project Forecast report.' },
                          { key: 'profitabilityReport' as const, label: 'Profitability Analysis Report', description: 'Access to the Profitability Analysis report.' },
                        ].map(({ key, label, description }) => (
                          <PermToggleCard
                            key={key}
                            label={label}
                            description={description}
                            checked={role.permissions.reports[key]}
                            onChange={(val) => updatePermission('reports', { [key]: val })}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </AccordionSection>
              </TabsContent>

              {/* ════════════════════════════════════════════════════════════
                  Settings Tab
              ════════════════════════════════════════════════════════════ */}
              <TabsContent value="settings" className="mt-0 space-y-4">
                <AccordionSection
                  icon={<Settings2 className="h-5 w-5 text-primary" />}
                  title="Settings Access"
                  description="Control which settings sections are accessible to users with this role."
                >
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'generalSettings' as const, label: 'General Settings', description: 'Access to company-wide general settings.' },
                      { key: 'configureAuditTrail' as const, label: 'Configure Audit Trail Settings', description: 'Access to configure what is tracked in the audit trail.' },
                      { key: 'projectSettings' as const, label: 'Project Settings', description: 'Access to project configuration settings.' },
                      { key: 'integrations' as const, label: 'Integrations', description: 'Access to manage third-party integrations.' },
                      { key: 'rolesAndPermissions' as const, label: 'Roles & Permissions', description: 'Access to manage roles and user permissions.' },
                      { key: 'templates' as const, label: 'Invoice & Email Templates', description: 'Access to manage invoice and email templates.' },
                      { key: 'notifications' as const, label: 'Notifications', description: 'Access to configure notification settings.' },
                    ].map(({ key, label, description }) => (
                      <PermToggleCard
                        key={key}
                        label={label}
                        description={description}
                        checked={role.permissions.settings[key]}
                        onChange={(val) => updatePermission('settings', { [key]: val })}
                      />
                    ))}
                  </div>
                </AccordionSection>
              </TabsContent>

              {/* ════════════════════════════════════════════════════════════
                  Users Tab
              ════════════════════════════════════════════════════════════ */}
              <TabsContent value="users" className="mt-0 space-y-4">
                <AccordionSection
                  icon={<Users className="h-5 w-5 text-primary" />}
                  title="Assigned Users"
                  description="Users currently assigned to this role."
                >
                  <div className="space-y-4">
                    {/* Header row: Add Users button */}
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-muted-foreground">
                        {users.length === 0
                          ? 'No users assigned yet.'
                          : `${users.length} user${users.length !== 1 ? 's' : ''} assigned`}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsAddDialogOpen(true)}
                        disabled={isAssigning}
                        className="gap-1.5"
                      >
                        <UserPlus className="h-4 w-4" />
                        Add Users
                      </Button>
                    </div>

                    {/* Users table with bulk-select */}
                    {users.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground text-sm">
                        No users assigned to this role yet.
                      </div>
                    ) : (
                      <div className="-mx-6 -mb-5 border-t border-border overflow-hidden">
                        <div className="p-0">
                          <Table>
                            <TableHeader divided>
                              <TableRow className="bg-background hover:!bg-background">
                                <TableHead className="w-10 px-0 text-center">
                                  <Checkbox
                                    checked={
                                      users.length > 0 &&
                                      users.every((u) => bulkSelectedIds.has(u.id))
                                    }
                                    data-state={
                                      users.some((u) => bulkSelectedIds.has(u.id)) &&
                                      !users.every((u) => bulkSelectedIds.has(u.id))
                                        ? 'indeterminate'
                                        : undefined
                                    }
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setBulkSelectedIds(new Set(users.map((u) => u.id)));
                                      } else {
                                        setBulkSelectedIds(new Set());
                                      }
                                    }}
                                    aria-label="Select all users"
                                  />
                                </TableHead>
                                <TableHead className="text-xs font-medium">Name</TableHead>
                                <TableHead className="text-xs font-medium">Email</TableHead>
                                <TableHead className="text-xs font-medium">Last Active</TableHead>
                                <TableHead className="text-xs font-medium text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {users.map((user) => {
                                const isSelected = bulkSelectedIds.has(user.id);
                                return (
                                  <TableRow
                                    key={user.id}
                                    className={cn(
                                      'cursor-pointer text-xs',
                                      isSelected
                                        ? 'bg-primary/5 hover:!bg-primary/10'
                                        : 'odd:bg-white even:bg-neutral-50 odd:hover:!bg-white even:hover:!bg-neutral-50',
                                    )}
                                    onClick={() => {
                                      setBulkSelectedIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(user.id)) next.delete(user.id);
                                        else next.add(user.id);
                                        return next;
                                      });
                                    }}
                                  >
                                    <TableCell className="w-10 px-0 text-center" onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() => {
                                          setBulkSelectedIds((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(user.id)) next.delete(user.id);
                                            else next.add(user.id);
                                            return next;
                                          });
                                        }}
                                        aria-label={`Select ${user.name}`}
                                      />
                                    </TableCell>
                                    <TableCell className="font-medium">{user.name}</TableCell>
                                    <TableCell className="text-muted-foreground">
                                      {user.email}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                      {user.lastActive
                                        ? new Date(user.lastActive).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                          })
                                        : 'Never'}
                                    </TableCell>
                                    <TableCell
                                      className="text-right"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => setRemoveUserTarget(user)}
                                        aria-label={`Remove ${user.name}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Bulk remove floating bar */}
                    <BulkRemoveBar
                      selectedCount={bulkSelectedIds.size}
                      onRemove={() => setShowBulkRemoveConfirm(true)}
                      onClear={() => setBulkSelectedIds(new Set())}
                      isRemoving={isBulkRemoving}
                    />
                  </div>
                </AccordionSection>
              </TabsContent>
            </Tabs>
          </div>

          {/* ── Sticky Save Footer ────────────────────────────────────────────── */}
          {hasChanges && (
            <div className="sticky bottom-0 z-10 bg-background border-t mt-0 py-4 px-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Unsaved changes</p>
                  <p className="text-xs text-muted-foreground">
                    Don't forget to save your changes before leaving this page
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleDiscard} disabled={isSaving}>
                    <X className="h-4 w-4 mr-2" />
                    Discard
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    size="lg"
                    className="shadow-md hover:shadow-lg transition-shadow"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add users dialog ──────────────────────────────────────────────── */}
      <AddUsersDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        roleName={role.name}
        availableEmployees={availableEmployees}
        isLoadingEmployees={isLoadingEmployees}
        onUsersAdded={handleUsersAdded}
      />

      {/* ── Single remove user confirmation ──────────────────────────────── */}
      <AlertDialog
        open={!!removeUserTarget}
        onOpenChange={(open) => !open && setRemoveUserTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user from role?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{removeUserTarget?.name}</strong> from this
              role? They will lose all permissions associated with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Bulk remove confirmation ──────────────────────────────────────── */}
      <AlertDialog
        open={showBulkRemoveConfirm}
        onOpenChange={(open) => !open && setShowBulkRemoveConfirm(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {bulkSelectedIds.size} user{bulkSelectedIds.size !== 1 ? 's' : ''} from role?
            </AlertDialogTitle>
            <AlertDialogDescription>
              These {bulkSelectedIds.size === 1 ? 'user' : `${bulkSelectedIds.size} users`} will
              lose all permissions associated with the <strong>{role.name}</strong> role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkRemove}
              disabled={isBulkRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isBulkRemoving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Removing…
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Reassign user confirmation ────────────────────────────────────── */}
      <AlertDialog
        open={!!reassignTarget}
        onOpenChange={(open) => !open && setReassignTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing role?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace <strong>{reassignTarget?.employee.name}</strong>&apos;s current role
              of <strong>{reassignTarget?.currentRoleName}</strong> with <strong>{role.name}</strong>.
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReassignUser}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsLayout>
  );
}
